import React, { useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { Modal, Select, Typography, TypographyVariant } from 'components/core';
import { UserStatus } from 'modules/multi-user/enums/user-status';
import { AdminKycStatus } from 'modules/multi-user/enums/admin-kyc-status';
import { UserRole } from 'modules/2023-q4/rbac/enums/user-role';
import { FeatureFlag, FeatureFlags } from 'utils/feature-flags';
import { UserPermissionsRequired } from 'modules/2023-q4/rbac/components/user-permissions-required/user-permissions-required';
import { useBusiness } from 'hooks/business/use-business';
import { useUser } from 'hooks/auth/use-user';
import { useRemoveTeamMember } from 'modules/multi-user/hooks/use-remove-team-member';
import { useUpdateTeamMemberRole } from 'modules/multi-user/hooks/use-update-team-member-role';
import { useCancelInvitation } from 'modules/multi-user/hooks/use-cancel-invitation';
import { useCancelPendingAdminKyc } from 'modules/multi-user/hooks/use-cancel-pending-admin-kyc';
import { Toast } from 'components/layouts';
import {
  APPROVAL_HOLD_MESSAGE,
  isApprovalHold,
} from 'modules/settings/pages/approvals/is-approval-hold';
import { Styled } from './user-row.style';

const ACTION_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support if the issue persists.';

const getActionErrorMessage = (error: unknown): string => {
  const apiError = error as {
    response?: {
      data?: {
        errors?: Array<{ error_message?: string } | string>;
        message?: string;
      };
    };
  };

  const firstError = apiError.response?.data?.errors?.[0];

  if (typeof firstError === 'string') {
    return firstError;
  }

  return (
    firstError?.error_message ||
    apiError.response?.data?.message ||
    ACTION_ERROR_MESSAGE
  );
};

const ROLE_OPTIONS = [
  { label: 'Administrator', value: UserRole.ADMIN },
  { label: 'Read Only', value: UserRole.READ_ONLY },
];

interface UserRowProps {
  user: {
    id?: string;
    personId?: string;
    email: string;
    firstName: string;
    inviteStatus: UserStatus;
    lastName: string;
    role: UserRole;
    adminKycStatus?: AdminKycStatus;
  };
}

interface ConfirmActionModalProps {
  confirmLabel: string;
  description: React.ReactNode;
  errorMessage: string | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
}

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  confirmLabel,
  description,
  errorMessage,
  isLoading,
  onClose,
  onConfirm,
  open,
  title,
}) => {
  return (
    <Modal onClose={onClose} open={open}>
      <Box p={4}>
        <Typography variant={TypographyVariant.Header4}>{title}</Typography>

        <Box my={3}>{description}</Box>

        {errorMessage && (
          <Box mb={3}>
            <Typography color="amaranth" variant={TypographyVariant.Paragraph2}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        <Grid container justify="flex-end" spacing={2}>
          <Grid item>
            <Styled.ModalCancelButton onClick={onClose} variant="text">
              Cancel
            </Styled.ModalCancelButton>
          </Grid>
          <Grid item>
            <Styled.ModalConfirmButton disabled={isLoading} onClick={onConfirm}>
              {confirmLabel}
            </Styled.ModalConfirmButton>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

const getDisplayName = (user: UserRowProps['user']) =>
  `${user.firstName ? user.firstName : ''} ${
    user.lastName ? user.lastName : ''
  }`.trim() || user.email;

export const UserRow: React.FC<UserRowProps> = ({ user }) => {
  const { data: business } = useBusiness();
  const { data: currentUser } = useUser(business?.Id);
  const removeTeamMember = useRemoveTeamMember();
  const updateTeamMemberRole = useUpdateTeamMemberRole();
  const cancelInvitation = useCancelInvitation();
  const cancelPendingAdminKyc = useCancelPendingAdminKyc();
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelKycModalOpen, setCancelKycModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isInvited = user.inviteStatus === UserStatus.Invited;
  // Only PENDING changes the Team UI (lock role as Administrator, show the
  // substatus). Null, APPROVED, and REJECTED all fall through to the stored
  // relationship role. Null is the usual value for founding/onboarded admins
  // who never had a team KYC review — it is not an approval, but it looks the
  // same as APPROVED on this page.
  const isAdminPendingKyc = user.adminKycStatus === AdminKycStatus.Pending;
  const canRemove =
    !!user.id && !!currentUser?.Id && user.id !== currentUser.Id;
  const canCancelInvite = isInvited && !!user.personId;
  const canManageRole = !!user.id;
  const canCancelPendingKyc = isAdminPendingKyc && canRemove;

  const handleCloseRemoveModal = () => {
    setRemoveModalOpen(false);
    setErrorMessage(null);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setErrorMessage(null);
  };

  const handleCloseCancelKycModal = () => {
    setCancelKycModalOpen(false);
    setErrorMessage(null);
  };

  const handleRemove = async () => {
    if (!user.id) {
      return;
    }

    setErrorMessage(null);

    try {
      await removeTeamMember.mutateAsync({ memberId: user.id });
      handleCloseRemoveModal();
    } catch (error) {
      setErrorMessage(getActionErrorMessage(error));
    }
  };

  const handleCancelInvite = async () => {
    if (!user.personId) {
      return;
    }

    setErrorMessage(null);

    try {
      await cancelInvitation.mutateAsync({ personId: user.personId });
      handleCloseCancelModal();
    } catch (error) {
      setErrorMessage(getActionErrorMessage(error));
    }
  };

  const handleCancelPendingKyc = async () => {
    if (!user.id) {
      return;
    }

    setErrorMessage(null);

    try {
      await cancelPendingAdminKyc.mutateAsync({ memberId: user.id });
      handleCloseCancelKycModal();
    } catch (error) {
      setErrorMessage(getActionErrorMessage(error));
    }
  };

  const handleRoleChange = async (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    if (!user.id) {
      return;
    }

    const role = event.target.value as UserRole.ADMIN | UserRole.READ_ONLY;

    if (role === user.role) {
      return;
    }

    try {
      const data = await updateTeamMemberRole.mutateAsync({
        memberId: user.id,
        role,
      });

      if (isApprovalHold(data ?? {})) {
        Toast.show('info', APPROVAL_HOLD_MESSAGE);
      }
    } catch {
      // Error is surfaced via the global react-query error toast.
    }
  };

  const displayedRole = isAdminPendingKyc ? UserRole.ADMIN : user.role;

  const pendingKycNote = isAdminPendingKyc && (
    <Typography variant={TypographyVariant.SubHeader3}>
      Pending KYC review
      {canCancelPendingKyc && (
        <Styled.CancelKycLink
          onClick={() => {
            setErrorMessage(null);
            setCancelKycModalOpen(true);
          }}
        >
          Cancel
        </Styled.CancelKycLink>
      )}
    </Typography>
  );

  const roleDisplay = (
    <Box textAlign="center">
      {displayedRole === UserRole.READ_ONLY ? 'Read Only' : 'Administrator'}
      {pendingKycNote}
    </Box>
  );

  const roleSelector = canManageRole ? (
    <UserPermissionsRequired roles={[UserRole.ADMIN]}>
      <Box textAlign="center">
        <Select
          disabled={updateTeamMemberRole.isLoading || isAdminPendingKyc}
          onChange={handleRoleChange}
          options={ROLE_OPTIONS}
          value={displayedRole}
        />
        {pendingKycNote}
      </Box>
    </UserPermissionsRequired>
  ) : (
    roleDisplay
  );

  const displayName = getDisplayName(user);

  return (
    <Box mb={3}>
      <Grid container alignItems="center">
        <Grid item sm={1}>
          <Box mr={3}>
            <Styled.UserAvatar />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant={TypographyVariant.SubHeader1}>
            {displayName}
          </Typography>

          <Typography variant={TypographyVariant.SubHeader3}>
            {user.email}
          </Typography>
        </Grid>

        <FeatureFlag
          disabled={
            <Grid item xs={6} sm={3}>
              {roleDisplay}
            </Grid>
          }
          enabled={
            <Grid item xs={6} sm={3}>
              {roleSelector}
            </Grid>
          }
          flag={FeatureFlags.REACT_APP_2023_Q4_USER_ROLES_ENABLED}
        />

        <Grid item xs={12} sm={2}>
          <Box textAlign="center">
            {canCancelInvite ? (
              <FeatureFlag
                disabled={
                  <Styled.RemoveLink
                    onClick={() => {
                      setErrorMessage(null);
                      setCancelModalOpen(true);
                    }}
                  >
                    Cancel Invite
                  </Styled.RemoveLink>
                }
                enabled={
                  <UserPermissionsRequired roles={[UserRole.ADMIN]}>
                    <Styled.RemoveLink
                      onClick={() => {
                        setErrorMessage(null);
                        setCancelModalOpen(true);
                      }}
                    >
                      Cancel Invite
                    </Styled.RemoveLink>
                  </UserPermissionsRequired>
                }
                flag={FeatureFlags.REACT_APP_2023_Q4_USER_ROLES_ENABLED}
              />
            ) : (
              <FeatureFlag
                disabled={
                  <Styled.RemoveLink
                    $disabled={!canRemove}
                    onClick={() => {
                      if (!canRemove) {
                        return;
                      }

                      setErrorMessage(null);
                      setRemoveModalOpen(true);
                    }}
                  >
                    Remove
                  </Styled.RemoveLink>
                }
                enabled={
                  <UserPermissionsRequired roles={[UserRole.ADMIN]}>
                    <Styled.RemoveLink
                      $disabled={!canRemove}
                      onClick={() => {
                        if (!canRemove) {
                          return;
                        }

                        setErrorMessage(null);
                        setRemoveModalOpen(true);
                      }}
                    >
                      Remove
                    </Styled.RemoveLink>
                  </UserPermissionsRequired>
                }
                flag={FeatureFlags.REACT_APP_2023_Q4_USER_ROLES_ENABLED}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <ConfirmActionModal
        confirmLabel="Remove"
        description={
          <Typography variant={TypographyVariant.Paragraph1}>
            Are you sure you want to remove <b>{displayName}</b> ({user.email})
            from your team? They will lose access to this business in Treasure.
          </Typography>
        }
        errorMessage={errorMessage}
        isLoading={removeTeamMember.isLoading}
        onClose={handleCloseRemoveModal}
        onConfirm={handleRemove}
        open={removeModalOpen}
        title="Remove team member"
      />

      <ConfirmActionModal
        confirmLabel="Cancel Invite"
        description={
          <Typography variant={TypographyVariant.Paragraph1}>
            Are you sure you want to cancel the invitation for{' '}
            <b>{displayName}</b> ({user.email})? They will not be able to join
            this team unless invited again.
          </Typography>
        }
        errorMessage={errorMessage}
        isLoading={cancelInvitation.isLoading}
        onClose={handleCloseCancelModal}
        onConfirm={handleCancelInvite}
        open={cancelModalOpen}
        title="Cancel invitation"
      />

      <ConfirmActionModal
        confirmLabel="Cancel review"
        description={
          <Typography variant={TypographyVariant.Paragraph1}>
            Are you sure you want to cancel the administrator KYC review for{' '}
            <b>{displayName}</b> ({user.email})? They will remain Read Only.
          </Typography>
        }
        errorMessage={errorMessage}
        isLoading={cancelPendingAdminKyc.isLoading}
        onClose={handleCloseCancelKycModal}
        onConfirm={handleCancelPendingKyc}
        open={cancelKycModalOpen}
        title="Cancel KYC review"
      />
    </Box>
  );
};
